import { moonpayOrigins } from './constants';
import { isMoonPayCoreSdkLegacyConfig } from './helpers';
import {
  MoonPayCoreSdkConfig,
  MoonPayCoreSdkEnvironment,
  WidgetEnvironment,
  WidgetFlow,
} from './types/moonpayCoreSdkConfig';

export function stringifyQueryParam(value: any): string | null {
  if (!value) {
    return null;
  }

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return `${value}`;
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return null;
}

export function getOriginForConfig({
  environment,
  flow,
}: {
  environment: WidgetEnvironment;
  flow: WidgetFlow;
}): string | undefined {
  switch (flow) {
    case 'buy':
    case 'swapsCustomerSetup':
    case 'swap':
    case 'nft':
    case 'claim':
    case 'topup':
    case 'transactionTracker':
    case 'consumerKyc':
    case 'fiatBalanceDeposit':
      return moonpayOrigins[environment].buy;
    case 'sell':
    case 'withdraw':
    case 'fiatBalanceWithdraw':
      return moonpayOrigins[environment].sell;
    default:
      throw new Error(`Flow ${flow} is not supported`);
  }
}

export function getPathnameForConfig({
  flow,
}: {
  flow: WidgetFlow;
}): string | undefined {
  switch (flow) {
    case 'buy':
    case 'sell':
      return '/';
    case 'swap':
      return '/swaps';
    case 'swapsCustomerSetup':
      return '/verify/swaps';
    case 'consumerKyc':
      return '/consumer/kyc';
    case 'nft':
      return '/nft';
    case 'claim':
      return '/claim';
    case 'topup':
      return '/topup';
    case 'withdraw':
      return '/withdraw';
    case 'transactionTracker':
      return '/transaction_receipt';
    case 'fiatBalanceDeposit':
      return '/balance/deposit';
    case 'fiatBalanceWithdraw':
      return '/balance/withdraw';
    default:
      throw new Error(`Flow ${flow} is not supported`);
  }
}

interface GenerateWidgetUrlParams<
  TSdkEnvironment extends MoonPayCoreSdkEnvironment
> {
  config: MoonPayCoreSdkConfig;
  omitSignature?: boolean;
  sdkEnvironment?: TSdkEnvironment;
}

export function generateWidgetUrl<
  TSdkEnvironment extends MoonPayCoreSdkEnvironment
>({
  config,
  omitSignature = false,
  sdkEnvironment,
}: GenerateWidgetUrlParams<TSdkEnvironment>): URL {
  if (isMoonPayCoreSdkLegacyConfig(config)) {
    return new URL(config.params.url);
  }

  const origin =
    config.baseOrigin ||
    getOriginForConfig({
      environment: config.environment,
      flow: config.flow,
    });
  const pathname = getPathnameForConfig({ flow: config.flow });

  if (!pathname || !origin) {
    throw new Error('Invalid config');
  }

  const url = new URL(pathname, origin);

  Object.entries(config.params).forEach(([key, value]) => {
    const encodedValue = stringifyQueryParam(value);

    if (!encodedValue) {
      return;
    }

    url.searchParams.append(key, encodedValue);
  });

  url.searchParams.append('mpSdk', JSON.stringify(sdkEnvironment));

  if (omitSignature) {
    url.searchParams.delete('signature');
  }

  if (config.auth) {
    const oneTimeTokenUrl = new URL(
      `/oauth/app/${config.auth.token}`,
      url.origin,
    );

    oneTimeTokenUrl.search = url.search;

    // In the OAuthApp screen we extract path and query params from redirectTo
    // `oneTimeTokenUrl.search = url.search` is there for backward compatibility
    oneTimeTokenUrl.searchParams.append(
      'redirectTo',
      `${url.pathname}${url.search}`,
    );
    return oneTimeTokenUrl;
  }

  return url;
}
